import { proxyCustomElement, HTMLElement, createEvent, h } from '@stencil/core/internal/client';
import { c as BusinessFormClickActions } from './business-form-types.js';
import { C as ComponentErrorCodes, a as ComponentErrorSeverity } from './ComponentError.js';
import { a as checkProvisioningStatus } from './helpers.js';
import { S as StyledHost } from './styled-host.js';
import { d as defineCustomElement$y } from './custom-popper2.js';
import { d as defineCustomElement$x } from './form-control-checkbox2.js';
import { d as defineCustomElement$w } from './form-control-date2.js';
import { d as defineCustomElement$v } from './form-control-error-text2.js';
import { d as defineCustomElement$u } from './form-control-file2.js';
import { d as defineCustomElement$t } from './form-control-help-text2.js';
import { d as defineCustomElement$s } from './form-control-monetary2.js';
import { d as defineCustomElement$r } from './form-control-number-masked2.js';
import { d as defineCustomElement$q } from './form-control-select2.js';
import { d as defineCustomElement$p } from './form-control-text2.js';
import { d as defineCustomElement$o } from './form-control-tooltip2.js';
import { d as defineCustomElement$n } from './business-additional-questions-form-step.js';
import { d as defineCustomElement$m } from './business-additional-questions-form-step-core.js';
import { d as defineCustomElement$l } from './business-bank-account-form-step.js';
import { d as defineCustomElement$k } from './business-core-info-form-step.js';
import { d as defineCustomElement$j } from './business-core-info-form-step-core.js';
import { d as defineCustomElement$i } from './business-document-upload-form-step.js';
import { d as defineCustomElement$h } from './business-document-upload-input-group.js';
import { d as defineCustomElement$g } from './business-documents-on-file.js';
import { d as defineCustomElement$f } from './business-owners-form-step.js';
import { d as defineCustomElement$e } from './business-owners-form-step-core.js';
import { d as defineCustomElement$d } from './business-representative-form-inputs.js';
import { d as defineCustomElement$c } from './business-representative-form-step.js';
import { d as defineCustomElement$b } from './business-representative-form-step-core.js';
import { d as defineCustomElement$a } from './business-terms-conditions-form-step.js';
import { d as defineCustomElement$9 } from './business-terms-conditions-text.js';
import { d as defineCustomElement$8 } from './identity-address-form.js';
import { d as defineCustomElement$7 } from './legal-address-form-step.js';
import { d as defineCustomElement$6 } from './legal-address-form-step-core.js';
import { d as defineCustomElement$5 } from './owner-form.js';
import { d as defineCustomElement$4 } from './payment-provisioning-form-buttons.js';
import { d as defineCustomElement$3 } from './payment-provisioning-form-steps.js';
import { d as defineCustomElement$2 } from './owner-form-core2.js';
import { d as defineCustomElement$1 } from './owner-form-inputs2.js';

const PaymentProvisioningCore = /*@__PURE__*/ proxyCustomElement(class PaymentProvisioningCore extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.clickEvent = createEvent(this, "click-event", 7);
        this.errorEvent = createEvent(this, "error-event", 7);
        this.submitted = createEvent(this, "submitted", 7);
        this.setBusinessProvisioned = () => {
            this.getBusiness({
                onSuccess: (response) => {
                    this.businessProvisioned = checkProvisioningStatus(response.data);
                    if (this.businessProvisioned) {
                        this.errorEvent.emit({
                            message: 'A request to provision payments for this business has already been submitted.',
                            errorCode: ComponentErrorCodes.PROVISIONING_REQUESTED,
                            severity: ComponentErrorSeverity.INFO,
                        });
                    }
                },
                onError: ({ error, code, severity }) => {
                    this.errorEvent.emit({
                        message: error,
                        errorCode: code,
                        severity: severity
                    });
                }
            });
        };
        this.postProvisioningData = () => {
            this.postProvisioning({
                onSuccess: (response) => {
                    this.submitted.emit({ data: { response } });
                },
                onError: ({ error, code, severity }) => {
                    this.submitted.emit({ data: { error } });
                    this.errorEvent.emit({
                        message: error,
                        errorCode: code,
                        severity: severity
                    });
                }
            });
        };
        this.refs = [];
        this.incrementSteps = () => {
            if (this.currentStep < this.totalSteps) {
                return this.currentStep++;
            }
        };
        this.decrementSteps = () => { return this.currentStep--; };
        this.previousStepButtonOnClick = () => {
            this.clickEvent.emit({ name: BusinessFormClickActions.previousStep });
            this.decrementSteps();
        };
        this.nextStepButtonOnClick = (e, clickEventName) => {
            e.preventDefault();
            this.clickEvent.emit({ name: clickEventName });
            const currentStep = this.refs[this.currentStep];
            currentStep.validateAndSubmit({ onSuccess: this.incrementSteps });
        };
        this.loading = false;
        this.businessProvisioned = false;
        this.currentStep = 0;
        this.errorMessage = undefined;
        this.businessId = undefined;
        this.authToken = undefined;
        this.allowOptionalFields = false;
        this.formTitle = undefined;
        this.getBusiness = undefined;
        this.postProvisioning = undefined;
    }
    componentWillLoad() {
        this.getBusiness && this.setBusinessProvisioned();
        this.refs = [this.coreInfoRef, this.legalAddressRef, this.additionalQuestionsRef, this.representativeRef, this.ownersRef, this.bankAccountRef, this.documentUploadRef, this.termsRef];
    }
    get businessEndpoint() {
        return `entities/business/${this.businessId}`;
    }
    get formDisabled() {
        return this.loading || this.businessProvisioned;
    }
    get totalSteps() {
        return this.refs.length - 1;
    }
    get stepCounter() {
        return `${this.currentStep + 1} of ${this.totalSteps + 1}`;
    }
    handleFormLoading(event) {
        this.loading = event.detail;
    }
    render() {
        return (h(StyledHost, { key: '80f2730a6a4e273391888b086c3633a3dec012b8', exportparts: 'label,input,input-invalid' }, h("div", { key: 'b4e7be2fed14b404ee7bcbe42c3c1c54bb78fdb6', class: 'row gap-3' }, h("h1", { key: 'f477beb7934a94a74dfc6b8a9c18cc54a720f70e' }, this.formTitle), h("justifi-payment-provisioning-form-steps", { key: 'e74aa3a01cfe7550f4917d28ccda1aca211d9394', businessId: this.businessId, authToken: this.authToken, refs: this.refs, currentStep: this.currentStep, allowOptionalFields: this.allowOptionalFields, handleFormLoading: this.handleFormLoading, onFormCompleted: () => this.postProvisioningData() }), h("div", { key: '26a585ec4b6b677cb01ac17696269168887f4c76', class: 'd-flex justify-content-between align-items-center' }, h("div", { key: 'ca5ddc176f8c2eb3315620076b401e47f45039bd', class: 'd-flex align-items-center' }, this.stepCounter), h("justifi-payment-provisioning-form-buttons", { key: '8299387bf859b33c829d29d66947c3c053ffa53c', currentStep: this.currentStep, totalSteps: this.totalSteps, formLoading: this.loading, formDisabled: this.formDisabled, previousStepButtonOnClick: this.previousStepButtonOnClick, nextStepButtonOnClick: this.nextStepButtonOnClick })))));
    }
}, [1, "justifi-payment-provisioning-core", {
        "businessId": [1, "business-id"],
        "authToken": [1, "auth-token"],
        "allowOptionalFields": [4, "allow-optional-fields"],
        "formTitle": [1, "form-title"],
        "getBusiness": [16],
        "postProvisioning": [16],
        "loading": [32],
        "businessProvisioned": [32],
        "currentStep": [32],
        "errorMessage": [32]
    }, [[0, "formLoading", "handleFormLoading"]]]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["justifi-payment-provisioning-core", "custom-popper", "form-control-checkbox", "form-control-date", "form-control-error-text", "form-control-file", "form-control-help-text", "form-control-monetary", "form-control-number-masked", "form-control-select", "form-control-text", "form-control-tooltip", "justifi-additional-questions-form-step", "justifi-additional-questions-form-step-core", "justifi-business-bank-account-form-step", "justifi-business-core-info-form-step", "justifi-business-core-info-form-step-core", "justifi-business-document-upload-form-step", "justifi-business-document-upload-input-group", "justifi-business-documents-on-file", "justifi-business-owners-form-step", "justifi-business-owners-form-step-core", "justifi-business-representative-form-inputs", "justifi-business-representative-form-step", "justifi-business-representative-form-step-core", "justifi-business-terms-conditions-form-step", "justifi-business-terms-conditions-text", "justifi-identity-address-form", "justifi-legal-address-form-step", "justifi-legal-address-form-step-core", "justifi-owner-form", "justifi-payment-provisioning-form-buttons", "justifi-payment-provisioning-form-steps", "owner-form-core", "owner-form-inputs"];
    components.forEach(tagName => { switch (tagName) {
        case "justifi-payment-provisioning-core":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, PaymentProvisioningCore);
            }
            break;
        case "custom-popper":
            if (!customElements.get(tagName)) {
                defineCustomElement$y();
            }
            break;
        case "form-control-checkbox":
            if (!customElements.get(tagName)) {
                defineCustomElement$x();
            }
            break;
        case "form-control-date":
            if (!customElements.get(tagName)) {
                defineCustomElement$w();
            }
            break;
        case "form-control-error-text":
            if (!customElements.get(tagName)) {
                defineCustomElement$v();
            }
            break;
        case "form-control-file":
            if (!customElements.get(tagName)) {
                defineCustomElement$u();
            }
            break;
        case "form-control-help-text":
            if (!customElements.get(tagName)) {
                defineCustomElement$t();
            }
            break;
        case "form-control-monetary":
            if (!customElements.get(tagName)) {
                defineCustomElement$s();
            }
            break;
        case "form-control-number-masked":
            if (!customElements.get(tagName)) {
                defineCustomElement$r();
            }
            break;
        case "form-control-select":
            if (!customElements.get(tagName)) {
                defineCustomElement$q();
            }
            break;
        case "form-control-text":
            if (!customElements.get(tagName)) {
                defineCustomElement$p();
            }
            break;
        case "form-control-tooltip":
            if (!customElements.get(tagName)) {
                defineCustomElement$o();
            }
            break;
        case "justifi-additional-questions-form-step":
            if (!customElements.get(tagName)) {
                defineCustomElement$n();
            }
            break;
        case "justifi-additional-questions-form-step-core":
            if (!customElements.get(tagName)) {
                defineCustomElement$m();
            }
            break;
        case "justifi-business-bank-account-form-step":
            if (!customElements.get(tagName)) {
                defineCustomElement$l();
            }
            break;
        case "justifi-business-core-info-form-step":
            if (!customElements.get(tagName)) {
                defineCustomElement$k();
            }
            break;
        case "justifi-business-core-info-form-step-core":
            if (!customElements.get(tagName)) {
                defineCustomElement$j();
            }
            break;
        case "justifi-business-document-upload-form-step":
            if (!customElements.get(tagName)) {
                defineCustomElement$i();
            }
            break;
        case "justifi-business-document-upload-input-group":
            if (!customElements.get(tagName)) {
                defineCustomElement$h();
            }
            break;
        case "justifi-business-documents-on-file":
            if (!customElements.get(tagName)) {
                defineCustomElement$g();
            }
            break;
        case "justifi-business-owners-form-step":
            if (!customElements.get(tagName)) {
                defineCustomElement$f();
            }
            break;
        case "justifi-business-owners-form-step-core":
            if (!customElements.get(tagName)) {
                defineCustomElement$e();
            }
            break;
        case "justifi-business-representative-form-inputs":
            if (!customElements.get(tagName)) {
                defineCustomElement$d();
            }
            break;
        case "justifi-business-representative-form-step":
            if (!customElements.get(tagName)) {
                defineCustomElement$c();
            }
            break;
        case "justifi-business-representative-form-step-core":
            if (!customElements.get(tagName)) {
                defineCustomElement$b();
            }
            break;
        case "justifi-business-terms-conditions-form-step":
            if (!customElements.get(tagName)) {
                defineCustomElement$a();
            }
            break;
        case "justifi-business-terms-conditions-text":
            if (!customElements.get(tagName)) {
                defineCustomElement$9();
            }
            break;
        case "justifi-identity-address-form":
            if (!customElements.get(tagName)) {
                defineCustomElement$8();
            }
            break;
        case "justifi-legal-address-form-step":
            if (!customElements.get(tagName)) {
                defineCustomElement$7();
            }
            break;
        case "justifi-legal-address-form-step-core":
            if (!customElements.get(tagName)) {
                defineCustomElement$6();
            }
            break;
        case "justifi-owner-form":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "justifi-payment-provisioning-form-buttons":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "justifi-payment-provisioning-form-steps":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "owner-form-core":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
        case "owner-form-inputs":
            if (!customElements.get(tagName)) {
                defineCustomElement$1();
            }
            break;
    } });
}
defineCustomElement();

export { PaymentProvisioningCore as P, defineCustomElement as d };
